import React, { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ClassesContext from '../../context/ClassesContext';
import { createClass } from '../../utils/classApi';
import './Classes.css';
import Footer from '../Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ns-dashboard-api.kabilah.com';

export const SEMESTER_TAG_OPTIONS = ['Fall', 'Spring', 'Summer'];

const CURRENT_YEAR = new Date().getFullYear();
export const CLASS_YEAR_OPTIONS = [-1, 0, 1, 2, 3].map((offset) => String(CURRENT_YEAR + offset));

// The semester + year are stored together as a single "tag" string (e.g. "Fall 2026")
// so no backend schema change was needed to add the year — split/combine here instead.
export const combineSemesterYear = (semester, year) => {
  if (semester && year) return `${semester} ${year}`;
  return semester || year || '';
};

export const splitSemesterYear = (tag) => {
  if (!tag) return { semester: '', year: '' };
  const parts = tag.trim().split(' ');
  const last = parts[parts.length - 1];
  if (parts.length > 1 && /^\d{4}$/.test(last)) {
    return { semester: parts.slice(0, -1).join(' '), year: last };
  }
  return { semester: tag, year: '' };
};

function CreateClassRow({ onCancel, onCreated }) {
  const [name, setName] = useState('');
  const [semester, setSemester] = useState('');
  const [year, setYear] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { onCancel(); return; }
    if (!semester) { setError('Select a semester.'); return; }
    if (!year) { setError('Select a year.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await createClass(trimmed, combineSemesterYear(semester, year));
      onCreated(res.data);
    } catch {
      setError('Could not create the class. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="class-card class-card-creating">
      <div className="class-card-main">
        <input
          ref={inputRef}
          className="class-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Class name (e.g. NUR 301)"
          disabled={saving}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') onCancel();
          }}
        />
        <select
          className="class-tag-select"
          aria-label="Semester"
          value={semester}
          onChange={(e) => setSemester(e.target.value)}
          disabled={saving}
        >
          <option value="" disabled>Select semester</option>
          {SEMESTER_TAG_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <select
          className="class-tag-select"
          aria-label="Year"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          disabled={saving}
        >
          <option value="" disabled>Select year</option>
          {CLASS_YEAR_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        {error && <span className="review-field-error">{error}</span>}
      </div>
      <div className="class-card-actions">
        <button className="title-button" onClick={commit} disabled={saving || !name.trim() || !semester || !year}>
          {saving ? 'Creating...' : 'Create'}
        </button>
        <button className="upload-cancel-btn" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

function Classes({ isTeacher }) {
  const navigate = useNavigate();
  const { classes, loading, setClasses } = useContext(ClassesContext);
  const [creating, setCreating] = useState(false);
  // classes' embedded students are just {id, name} — cross-reference against
  // the full roster (which has `tag`) to split each class's headcount into
  // real vs demo accounts.
  const [tagByStudentId, setTagByStudentId] = useState(new Map());

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/students`)
      .then((res) => {
        if (Array.isArray(res.data)) {
          setTagByStudentId(new Map(res.data.map((s) => [s.id, s.tag])));
        }
      })
      .catch(() => {});
  }, []);

  const handleCreated = (newClass) => {
    const normalized = { ...newClass, students: newClass.students || [], cases: newClass.cases || [] };
    setClasses((prev) => [...prev, normalized]);
    setCreating(false);
    navigate(`/admin/classes/${normalized.id}`);
  };

  return (
    <div className="root-container">
      <div className="title-container">
        <div className="title-left">
          <div className="title-name-container">
            <header className="name-header">Classes</header>
          </div>
        </div>
        {!isTeacher && (
          <div className="reload-button-container">
            <button className="reload-button" onClick={() => setCreating(true)} disabled={creating}>
              + Create Class
            </button>
          </div>
        )}
      </div>

      <div className="component-container">
        <div className="classes-content">
          {loading ? (
            <div className="cases-loading">Loading classes...</div>
          ) : (
            <div className="classes-list">
              {creating && (
                <CreateClassRow onCancel={() => setCreating(false)} onCreated={handleCreated} />
              )}
              {classes.length === 0 && !creating && (
                <p className="chat-card-hint">No classes yet — click "+ Create Class" to add one.</p>
              )}
              {classes.map((cls) => {
                const clsStudents = cls.students || [];
                const realCount = clsStudents.filter((s) => tagByStudentId.get(s.id) !== 'demo').length;
                const demoCount = clsStudents.length - realCount;
                return (
                  <div key={cls.id} className="class-card">
                    <div className="class-card-main">
                      <div className="class-name-row">
                        <span className="class-name">{cls.name}</span>
                        {cls.tag && <span className="class-tag-chip">{cls.tag}</span>}
                      </div>
                      <div className="class-meta">
                        <span className="class-meta-item">{realCount} student accounts</span>
                        <span className="class-meta-divider">·</span>
                        <span className="class-meta-item">{demoCount} demo accounts</span>
                        <span className="class-meta-divider">·</span>
                        <span className="class-meta-item">{(cls.cases || []).length} simulation cases</span>
                      </div>
                    </div>
                    <div className="class-card-actions">
                      <button className="title-button" onClick={() => navigate(`/admin/classes/${cls.id}`)}>{isTeacher ? 'View' : 'Manage'}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Classes;
